export const KeyCode = Object.freeze({
    "Shift": "ShiftLeft",
    "Esc": "Escape"
});

/**
 * Returns a root path to be used by Repositories, e.g. if current page is {@code https://blah.com/appcontext/page},
 * we need to get {@code https://blah.com/appcontext}.
 *
 * <p>
 * Needed because the app could be deployed either at '/', or at some other context like '/Prod', which is typical
 * for AWS deployments. In the latter case we can't just access `/api` - we need to change it to `/Prod/api`.
 *
 * @param pagePathInAddressBar whatever the page looks like in browser. If it's root, then pass empty string. If you
 *                             have 'page.html' in the URL, then pass that 'page.html'. This part is going to be
 *                             removed from the current URL.
 * @returns {string} an absolute URL without {@code pagePathInAddressBar} and without extra slashes. This path
 *                   can be then used to call API.
 */
export const getApiRootPath = (pagePathInAddressBar) => {
    let path = window.location.pathname;
    path = path.replace(pagePathInAddressBar, ''); //removing current page name because when accessing API the page shouldn't be in the URL
    while (path.startsWith('/'))
        // after removing the pageName from the URL, we may end up with multiple `//`, so need a while-loop
        path = path.substring(1);
    while (path.endsWith('/'))
        path = path.substring(0, path.length - 1);
    if (path.length !== 0) // returning slash back for non-root context, so that it's separated from the host
        path = '/' + path;
    return window.location.origin + path;
}

export const assertNotNull = (o) => {
    if(o === undefined || o === null)
        throw new Error("Value must have been filled: " + o);
    return o;
}
export const defaultIfNull = (o, defaultValue) => {
    if(o === undefined || o === null)
        return defaultValue;
    return o;
}

export const plural = (n, word, wordPlural = null) => {
    if(n === 1)
        return word;
    if(wordPlural)
        return wordPlural;
    return word + "s";
}

/**
 * To be used when generating HTML via string interpolation e.g. {@code safeHtml`<div>${variables}</div>`} -
 * these variables will go through this function and have to be escaped should such string be shown on UI.
 *
 * @param {string[]} strings
 * @param {string} values
 * @returns {string}
 */
export const safeHtml = (strings, ...values) => {
    let result = strings[0];
    for (let i = 0; i < values.length; i++) {
        result += safeText(String(values[i]));
        result += strings[i + 1];
    }
    return result;
}

/**
 * @param {string} unsafe
 * @returns {string}
 */
export const safeText = (unsafe) => {
    if (!unsafe)
        return ''
    return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}

/**
 * @param {string} one
 * @param {string} another
 * @returns A negative number if one occurs before another;
 *          positive if the one occurs after another;
 *          0 if they are equivalent.
 * */
export const compareStringsByCodePoints = (one, another) => {
    if (typeof one !== "string" || typeof another !== "string")
        throw new Error(`Both comparable must be strings. One: {type: ${typeof one}, value: ${one},
     another: {type: ${typeof another}, value: ${another}`);
    if (one.length !== another.length)
        return one.length - another.length;
    for (let i = 0; i < one.length; i++) {
        let cp1 = one.codePointAt(i), cp2 = another.codePointAt(i);
        if (cp1 !== cp2)
            return cp1 - cp2;
    }
    return 0;
}

/*
  Executes callback function with delay. Another function call with same timerId before delay end will reset the timer.
 */
export function doWithDelay(timerId, fun, delay) {
    if (timerId) clearTimeout(timerId);
    return setTimeout(fun, delay || 700);
}

/**
 *
 * @param {string} s
 * @returns {Date}
 */
export function parseTime(s) {
    const d = new Date(0);
    d.setUTCMilliseconds(Date.parse(s));
    return d;
}

/**
 * @param {Date} d
 * @return {string}
 */
export function formatDateTime(d) {
    return d.toLocaleString('en-Gb',
        {year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric'});
}

/**
 * @param {string} s
 * @returns {string}
 */
export function formattedDateFromString(s) {
    const d = new Date(0);
    d.setUTCMilliseconds(Date.parse(s));
    return d.toLocaleString('en-Gb',
        {year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'});
}
